import { useEffect, useMemo, useRef, useState } from "react";
import api, { detailFromAxiosError, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { toast } from "sonner";
import { ChevronDown, Copy, FileDown, Loader2, Upload } from "lucide-react";
import BuyerProfileSummary from "@/components/BuyerProfileSummary";
import LicenseInvoiceSection from "@/components/LicenseInvoiceSection";
import LicensePaymentProofSection from "@/components/LicensePaymentProofSection";
import {
  buildLicenseRequestFormDefaults,
  buildLicenseRequestPdfPayload,
  hasActiveCommercialLicense,
  hasAtLeastOneRequestedModule,
  isSubmissionBlocked,
  LICENSE_REQUEST_MSG_LEGAL_NOT_ACCEPTED,
  LICENSE_REQUEST_MSG_NO_MODULE,
  LICENSE_REQUEST_MSG_PDF_REGISTERED,
  moduleIdsForLicenseRequest,
  moduleLabel,
  parseLicenseRequestSubmitError,
  resolveCommercialModuleCatalog,
  resolveRequestableModuleIds,
  validateInvoiceFields,
} from "@/lib/commercialLicenseRequests";
import { normalizeScopeFormFields, validateScopeFields } from "@/lib/commercialLicenseScope";
import {
  createLicenseRequestPdf,
} from "@/lib/commercialLicenseRequestsApi";
import { useAuth } from "@/lib/auth";
import {
  defaultRequestedLicenseType,
  requestedLicenseTypeOptions,
} from "@/lib/licenseTypes";
import { buildPdfSolicitudCopyText } from "@/lib/licenseCommercialFlow";

function SectionBlock({ title, children, testId }) {
  return (
    <section className="rounded-sm border border-slate-200 bg-slate-50/40 p-3 space-y-2" data-testid={testId}>
      <h5 className="text-xs font-semibold text-[#0F2D5C] m-0">{title}</h5>
      {children}
    </section>
  );
}

/**
 * Flujo PDF/manual: solicitud por PDF + activación colapsable.
 */
export default function LicensePdfManualPanel({
  systemInfo,
  profileDefaults = null,
  onLicenseInstalled,
  submissionGate = null,
  onSubmissionGateChange,
  onRequestSubmitted,
  form: externalForm,
  setForm: externalSetForm,
  omitSections = {},
  showActivationSection = true,
  sharedLegal = null,
}) {
  const { user } = useAuth();
  const profile = profileDefaults || systemInfo?.profile || {};
  const useSharedLegal = Boolean(sharedLegal);
  const canProceedWithRequest = useSharedLegal ? sharedLegal.canProceedWithRequest : false;
  const agreementAcceptanceId = useSharedLegal ? sharedLegal.agreementAcceptanceId : "";
  const hasActiveLicense = hasActiveCommercialLicense(systemInfo);
  const submissionBlocked = isSubmissionBlocked(submissionGate);
  const catalog = useMemo(() => resolveCommercialModuleCatalog(systemInfo), [systemInfo]);
  const requestModuleIds = useMemo(() => moduleIdsForLicenseRequest(catalog), [catalog]);
  const requestableModuleIds = useMemo(() => resolveRequestableModuleIds(systemInfo), [systemInfo]);
  const licenseTypeOptions = useMemo(
    () => requestedLicenseTypeOptions(hasActiveLicense),
    [hasActiveLicense],
  );
  const [internalForm, setInternalForm] = useState(() => buildLicenseRequestFormDefaults(systemInfo, profile));
  const form = externalForm ?? internalForm;
  const setForm = externalSetForm ?? setInternalForm;
  const [busySolicPdf, setBusySolicPdf] = useState(false);
  const [busyRegister, setBusyRegister] = useState(false);
  const [busyActivacionPdf, setBusyActivacionPdf] = useState(false);
  const [busyLic, setBusyLic] = useState(false);
  const [licToken, setLicToken] = useState("");
  const [activationOpen, setActivationOpen] = useState(false);
  const [formError, setFormError] = useState("");
  const activacionPdfInputRef = useRef(null);

  useEffect(() => {
    if (externalForm != null) return;
    setInternalForm(buildLicenseRequestFormDefaults(systemInfo, profile));
  }, [systemInfo, profileDefaults, externalForm]);

  const toggleModule = (moduleId, checked) => {
    setForm((prev) => ({
      ...prev,
      requested_modules: { ...prev.requested_modules, [moduleId]: checked },
    }));
    setFormError("");
  };

  const validatePdfForm = () => {
    if (!hasActiveLicense && !hasAtLeastOneRequestedModule(form.requested_modules, systemInfo)) {
      return LICENSE_REQUEST_MSG_NO_MODULE;
    }
    const scopeErr = validateScopeFields(form);
    if (scopeErr) return scopeErr;
    const invoiceErr = validateInvoiceFields(form);
    if (invoiceErr) return invoiceErr;
    if (useSharedLegal && !canProceedWithRequest) {
      return LICENSE_REQUEST_MSG_LEGAL_NOT_ACCEPTED;
    }
    return null;
  };

  const buildPdfQueryParams = () => {
    const scope = normalizeScopeFormFields(form);
    const params = {
      estimated_users: scope.estimated_users,
      estimated_clients: scope.estimated_clients,
      estimated_users_range: scope.estimated_users_range,
      estimated_clients_range: scope.estimated_clients_range,
    };
    requestModuleIds.forEach((id) => {
      if (form.requested_modules?.[id]) params[id] = true;
    });
    if (form.requested_license_type) {
      params.requested_license_type = form.requested_license_type;
    }
    const fiscalAddress = (form.invoice_fiscal_address || profile.domicilio || "").trim();
    if (fiscalAddress) {
      params.domicilio = fiscalAddress;
    }
    return params;
  };

  const registerPdfRequest = async () => {
    if (useSharedLegal && !canProceedWithRequest) {
      setFormError(LICENSE_REQUEST_MSG_LEGAL_NOT_ACCEPTED);
      toast.error(LICENSE_REQUEST_MSG_LEGAL_NOT_ACCEPTED);
      document
        .querySelector('[data-testid="license-commercial-legal-section"]')
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const err = validatePdfForm();
    if (err) {
      setFormError(err);
      toast.error(err);
      return;
    }
    if (submissionBlocked) {
      toast.error(parseLicenseRequestSubmitError({ response: { status: 409, data: { detail: submissionGate } } }));
      return;
    }
    setFormError("");
    setBusyRegister(true);
    try {
      const payload = buildLicenseRequestPdfPayload(form, user, {
        hasActiveLicense,
        requestableModuleIds,
        catalog,
        agreementAcceptanceId,
      });
      await createLicenseRequestPdf(payload);
      toast.success(LICENSE_REQUEST_MSG_PDF_REGISTERED);
      onRequestSubmitted?.();
    } catch (e) {
      const msg = parseLicenseRequestSubmitError(e) || formatApiError(e.response?.data?.detail) || "No se pudo registrar la solicitud";
      setFormError(msg);
      toast.error(msg);
    } finally {
      setBusyRegister(false);
    }
  };

  const copySolicitudText = () => {
    if (useSharedLegal && !canProceedWithRequest) {
      setFormError(LICENSE_REQUEST_MSG_LEGAL_NOT_ACCEPTED);
      toast.error(LICENSE_REQUEST_MSG_LEGAL_NOT_ACCEPTED);
      document
        .querySelector('[data-testid="license-commercial-legal-section"]')
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const err = validatePdfForm();
    if (err) {
      setFormError(err);
      toast.error(err);
      return;
    }
    setFormError("");
    const text = buildPdfSolicitudCopyText({ profile, systemInfo, form });
    navigator.clipboard.writeText(text);
    toast.success("Texto de solicitud copiado");
  };

  const downloadSolicitudPdf = async () => {
    if (useSharedLegal && !canProceedWithRequest) {
      setFormError(LICENSE_REQUEST_MSG_LEGAL_NOT_ACCEPTED);
      toast.error(LICENSE_REQUEST_MSG_LEGAL_NOT_ACCEPTED);
      document
        .querySelector('[data-testid="license-commercial-legal-section"]')
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const err = validatePdfForm();
    if (err) {
      setFormError(err);
      toast.error(err);
      return;
    }
    setFormError("");
    setBusySolicPdf(true);
    try {
      const { data } = await api.get("/system/license/solicitud-pdf", {
        responseType: "blob",
        params: buildPdfQueryParams(),
      });
      const safeId = String(systemInfo?.installation_id || "visor").replace(/[^a-zA-Z0-9_-]/g, "-");
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `solicitud-licencia-${safeId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF de solicitud descargado. Revise que incluya los módulos y datos de factura en el texto copiado si los necesita.");
    } catch (e) {
      toast.error(
        (await detailFromAxiosError(e)) || formatApiError(e.response?.data?.detail) || "Error al generar el PDF",
      );
    } finally {
      setBusySolicPdf(false);
    }
  };

  const onImportActivacionLicenciaPdf = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Selecciona un archivo PDF");
      return;
    }
    setBusyActivacionPdf(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/system/license/import-activacion-pdf", fd);
      toast.success("Licencia activada desde el PDF");
      const { data: infoData } = await api.get("/system/info");
      onLicenseInstalled?.({ info: infoData, postInstallNotice: Boolean(data?.post_install_data_notice) });
    } catch (err) {
      toast.error(
        (await detailFromAxiosError(err)) ||
          formatApiError(err.response?.data?.detail) ||
          "No se pudo leer o validar el PDF de activación",
      );
    } finally {
      setBusyActivacionPdf(false);
    }
  };

  const installLicense = async () => {
    const t = licToken.trim();
    if (!t) {
      toast.error("Pega la clave de licencia");
      return;
    }
    setBusyLic(true);
    try {
      const { data } = await api.put("/system/license", { license_token: t });
      toast.success(data?.message || "Licencia instalada");
      const { data: infoData } = await api.get("/system/info");
      onLicenseInstalled?.({
        info: infoData,
        postInstallNotice: Boolean(data?.post_install_data_notice),
      });
      setLicToken("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo instalar la licencia");
    } finally {
      setBusyLic(false);
    }
  };

  const moduleIds = hasActiveLicense ? requestableModuleIds : requestModuleIds;
  const pdfActionsDisabled = submissionBlocked;

  return (
    <div className="space-y-4" data-testid="license-pdf-manual-panel">
      <p className="text-[11px] text-slate-600 m-0 leading-snug">
        Genere una solicitud en PDF o texto para enviarla a BNT. La activación con clave o PDF de respuesta va en la
        subsección al final, solo si ya la recibió.
      </p>

      {formError ? (
        <div className="rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-950">
          {formError}
        </div>
      ) : null}

      {!omitSections.profile ? (
      <SectionBlock title="Resumen del comprador" testId="license-pdf-profile-summary">
        <BuyerProfileSummary profile={profile} systemInfo={systemInfo} />
      </SectionBlock>
      ) : null}

      {!omitSections.licenseType ? (
      <SectionBlock title="Tipo de licencia solicitado" testId="license-pdf-type-section">
        <div className="max-w-md">
          <Select
            value={form.requested_license_type || defaultRequestedLicenseType(hasActiveLicense)}
            onValueChange={(v) => setForm((f) => ({ ...f, requested_license_type: v }))}
          >
            <SelectTrigger className="h-9 text-xs" data-testid="license-pdf-type-select">
              <SelectValue placeholder="Seleccione tipo de licencia" />
            </SelectTrigger>
            <SelectContent>
              {licenseTypeOptions.map((opt) => (
                <SelectItem key={opt.id} value={opt.id} className="text-xs">
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </SectionBlock>
      ) : null}

      {!omitSections.modules && moduleIds.length > 0 ? (
        <SectionBlock title="Módulos que solicita" testId="license-pdf-modules-section">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {moduleIds.map((moduleId) => (
              <label
                key={moduleId}
                className="flex items-start gap-2 rounded-sm border border-slate-200 bg-white px-3 py-2 text-[11px] cursor-pointer hover:bg-slate-50"
                data-testid={`license-pdf-module-${moduleId}`}
              >
                <Checkbox
                  checked={Boolean(form.requested_modules?.[moduleId])}
                  onCheckedChange={(v) => toggleModule(moduleId, v === true)}
                />
                <span>{moduleLabel(moduleId, catalog)}</span>
              </label>
            ))}
          </div>
        </SectionBlock>
      ) : null}

      {!omitSections.invoice ? (
      <SectionBlock title="Factura" testId="license-pdf-invoice-section">
        <LicenseInvoiceSection
          form={form}
          setForm={setForm}
          profile={profile}
          showModuleSummary
          testId="license-pdf-invoice-fields"
        />
      </SectionBlock>
      ) : null}

      {!omitSections.paymentProof ? (
        <LicensePaymentProofSection
          form={form}
          setForm={setForm}
          showPdfManualHint
          testId="license-pdf-payment-proof"
        />
      ) : null}

      <p className="text-[10px] text-slate-500 m-0 italic" data-testid="license-pdf-payment-hint">
        Si envía la solicitud por correo o WhatsApp, también puede incluir su comprobante de pago en ese envío.
      </p>

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="default"
          size="sm"
          className="h-9 text-xs bg-[#0F2D5C] hover:bg-[#0a2247] text-white"
          disabled={busyRegister || pdfActionsDisabled}
          onClick={registerPdfRequest}
          data-testid="license-pdf-register-request"
        >
          {busyRegister ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
          Registrar solicitud PDF/manual
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 text-xs border-[#0F2D5C] text-[#0F2D5C]"
          disabled={pdfActionsDisabled}
          onClick={copySolicitudText}
          data-testid="license-pdf-copy-text"
        >
          <Copy className="w-3.5 h-3.5 mr-1.5" />
          Copiar texto para solicitud
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 text-xs border-[#0F2D5C] text-[#0F2D5C]"
          disabled={busySolicPdf || pdfActionsDisabled}
          onClick={downloadSolicitudPdf}
          data-testid="license-pdf-download"
        >
          {busySolicPdf ? (
            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
          ) : (
            <FileDown className="w-3.5 h-3.5 mr-1.5" />
          )}
          Descargar PDF de solicitud
        </Button>
      </div>

      {showActivationSection ? (
      <Collapsible open={activationOpen} onOpenChange={setActivationOpen}>
        <CollapsibleTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-9 w-full justify-between text-xs font-semibold text-slate-800 border border-slate-200 bg-white hover:bg-slate-50"
            data-testid="license-pdf-activation-toggle"
          >
            Ya tengo mi clave o PDF de activación
            <ChevronDown className={`w-4 h-4 transition-transform ${activationOpen ? "rotate-180" : ""}`} />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-3 space-y-3" data-testid="license-pdf-activation-section">
          <p className="text-[10px] text-slate-600 m-0">
            Use esta sección solo cuando BNT o su distribuidor ya le entregó la clave o el PDF de activación.
          </p>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Label className="text-xs">Pegar clave de licencia</Label>
            <div>
              <input
                ref={activacionPdfInputRef}
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={onImportActivacionLicenciaPdf}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-[11px] border-emerald-600 text-emerald-800"
                disabled={busyActivacionPdf || busyLic}
                onClick={() => activacionPdfInputRef.current?.click()}
                data-testid="license-pdf-import-activation"
              >
                {busyActivacionPdf ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Upload className="w-3.5 h-3.5 mr-1.5" />
                )}
                Importar PDF de activación
              </Button>
            </div>
          </div>
          <textarea
            value={licToken}
            onChange={(e) => setLicToken(e.target.value)}
            rows={3}
            spellCheck={false}
            lang="en"
            placeholder="base64.firma — pega aquí el token que te enviaron"
            className="w-full rounded-sm border border-slate-300 px-3 py-2 text-xs font-mono resize-none"
            data-testid="license-pdf-paste-token"
          />
          <Button
            type="button"
            size="sm"
            disabled={busyLic}
            className="h-9 bg-[#3DA873] hover:bg-[#3DA873]/90 text-xs"
            onClick={installLicense}
            data-testid="license-pdf-install-token"
          >
            {busyLic ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
            {systemInfo?.license?.present ? "Reemplazar licencia" : "Instalar licencia"}
          </Button>
        </CollapsibleContent>
      </Collapsible>
      ) : null}
    </div>
  );
}
